import { useContext, useMemo, useState } from 'react'
import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'

import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { Auth } from '../../StateManagment/Auth'
import recordStatusChange from '../../helpers/statusHistory/recordStatusChange'
import Table from '../../components/common/table/Table'
import TableCell from '../../components/common/table/TableCell'
import { UITKOMST_OPTIONS, AUTO_REGISTER_DETAIL, registratieUpdate, woningtypeLabel, buildRegistratieHousehold } from '../../helpers/wervingsacties/registratieOptions'
import { filterRegistraties, registratieFilterOptions } from '../../helpers/wervingsacties/filterRegistraties'
import { ONBEKENDE_DIENST, dienstenOf } from '../../helpers/wervingsacties/resolveWervingsDiensten'
import RegistratieModal from './RegistratieModal'

const uitkomstLabel = (v) => UITKOMST_OPTIONS.find((o) => o.value === v)?.label || '—'

/**
 * De uitkomst met de diensten erachter, bijvoorbeeld 'Ja: Fixer, Energiecoach'.
 * Ook een verborgen dienst staat er met zijn naam; die kent dienstNamen.
 *
 * @param {object} r - Wervingsregistratie
 * @param {Object<string, string>} namen - Sleutel → naam (dienstNamen)
 * @returns {string}
 */
const uitkomstMetDiensten = (r, namen) => {
    const diensten = dienstenOf(r).map((key) => namen?.[key] || ONBEKENDE_DIENST)
    return diensten.length > 0 ? `${uitkomstLabel(r.Uitkomst)}: ${diensten.join(', ')}` : uitkomstLabel(r.Uitkomst)
}
const adresLabel = (r) => [r.Straatnaam, r.Huisnummer, r.HuisnummerToevoeging].filter(Boolean).join(' ')

// Buiten de component: een nieuwe array per render liet de tabel zijn eigen
// zoekfilter bij elke render opnieuw uitrekenen.
const COLUMNS = [
    { key: 'straat', label: 'ADRES' },
    { key: 'postcode', label: 'POSTCODE', searchable: false },
    { key: 'brief', label: 'BRIEF', searchable: false },
    { key: 'datumBrief', label: 'DATUM BRIEF', searchable: false },
    { key: 'deur', label: 'DEURGESPREK', searchable: false },
    { key: 'datum', label: 'DATUM GESPREK', searchable: false },
    { key: 'uitkomst', label: 'UITKOMST', searchable: false },
    { key: 'woning', label: 'WONING', searchable: false },
]

// The per-address registration table (the spreadsheet's street-level sheet).
// Rows are the campaign's in-scope addresses; clicking edit opens the editor.
//
// Filter op buurt en straat: met een hele wijk gekozen staan hier duizenden
// rijen, en een werver werkt straat voor straat. Filteren mag de rijen uit de
// tabel halen: er staat in een rij geen ongeopgeslagen invoer, bewerken gaat
// via RegistratieModal.
// AI-CONTEXT: See docs/pitfalls.md#ui-filtering-a-list-unmounts-unsaved-input

/**
 * @param {object} props
 * @param {Array<object>} props.registraties - De levende Wervingsregistraties van de actie
 * @param {Object<string, string>} props.buurtNames - Buurtcode → buurtnaam (useBuurtNames); leeg zolang hij laadt
 * @param {Array<{ key: string, label: string }>} props.diensten - De zichtbare diensten van de organisatie
 * @param {Object<string, string>} props.dienstNamen - Sleutel → naam, ook van verborgen diensten
 * @param {boolean} props.canEdit - Of de gebruiker het recht 'Huishoudens bewerken' heeft
 */
const Registratie = ({ registraties, buurtNames, diensten, dienstNamen, canEdit }) => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)
    const [editorOpen, setEditorOpen] = useState(false)
    const [selected, setSelected] = useState(null)
    const [buurtKeuze, setBuurtKeuze] = useState('')
    const [straatKeuze, setStraatKeuze] = useState('')

    // Een keuze die niet (meer) in de lijst staat, telt als 'alle': een straat
    // die op 'Gebied' is losgelaten, of een straat buiten de nieuw gekozen buurt.
    // Onthouden: bij een hele wijk zijn dit duizenden rijen, en dit scherm
    // rendert ook bij dingen die er niets mee te maken hebben (de melding
    // 'Opgeslagen', het openen van het bewerkvenster).
    const buurtOpties = useMemo(
        () => registratieFilterOptions(registraties, { buurtNames, buurt: '' }).buurten,
        [registraties, buurtNames],
    )
    const buurt = buurtOpties.some((o) => o.value === buurtKeuze) ? buurtKeuze : ''
    const straatOpties = useMemo(
        () => registratieFilterOptions(registraties, { buurtNames, buurt }).straten,
        [registraties, buurtNames, buurt],
    )
    const straat = straatOpties.some((o) => o.value === straatKeuze) ? straatKeuze : ''

    const items = useMemo(() => filterRegistraties(registraties, { buurt, straat }), [registraties, buurt, straat])

    const openEdit = (r) => {
        setSelected(r)
        setEditorOpen(true)
    }

    // Create a household (Adres) from a registration, pre-filled with the
    // address + contact gathered at the door. Mirrors Overview.addAdress, but
    // also seeds the already-geocoded Location so it doesn't re-geocode.
    const createHousehold = async (reg, form) => {
        const newId = uuid()
        // One-shot count for Position (best-effort ordering) — avoids a live
        // listener on the whole Adresses collection just for this rare action.
        const existing = await db.collection('Adresses').where('CompagnyID', '==', client).get()
        await db.collection('Adresses').doc().set({
            Title: '',
            ID: newId,
            Compagny: client,
            CompagnyID: client,
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            Research: [],
            Position: existing.size + 1,
            Status: 'planning',
            // Waarmee de bewoner aan de deur 'ja' zei, nu op het huishouden:
            // daar telt de pagina 'Huishoudens per status' ze per dienst.
            // Dezelfde regel als de registratierij (alleen bij een 'ja').
            // AI-CONTEXT: See docs/features/huishoudens-status-per-periode.md
            Diensten: dienstenOf(registratieUpdate(form)),
            // Adres, contact en type woning als vaste gegevens op het huishouden
            // (T11); het adres komt uit het register, dus straat en woonplaats
            // kloppen — ook de woonplaats die hier vroeger ontbrak.
            // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md
            ...buildRegistratieHousehold(reg, form),
            Location: {
                Postcode: reg.Postcode || '',
                Huisnummer: reg.Huisnummer || '',
                HuisnummerToevoeging: reg.HuisnummerToevoeging || '',
                Straatnaam: reg.Straatnaam || '',
                Woonplaats: reg.Woonplaats || '',
                BAGid: reg.BAGid || '',
                Lat: reg.Lat ?? null,
                Lng: reg.Lng ?? null,
                BuurtCode: reg.BuurtCode || '',
                WijkCode: reg.WijkCode || '',
                GeocodeStatus: 'ok',
                GeocodedAt: firebase.firestore.FieldValue.serverTimestamp(),
            },
        })

        // Beginregel van de statushistorie: hier begint het traject.
        await recordStatusChange({
            entityType: 'adress',
            entityID: newId,
            toStatus: 'planning',
            changedBy: auth?.ID,
        })

        // Alleen nog de eigen gegevens: de vaste staan hierboven op het huishouden.
        const defaultFields = [
            { Title: 'Bijzonderheden', Value: form.Opmerkingen || '' },
        ]
        for (let i = 0; i < defaultFields.length; i++) {
            await db.collection('PersonaData').add({
                ID: uuid(),
                PersonaID: newId,
                Title: defaultFields[i].Title,
                Value: defaultFields[i].Value,
                CompagnyID: client,
                Compagny: client,
                Position: i + 1,
                Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            })
        }
        return newId
    }

    const saveRegistratie = async (form) => {
        if (!selected) return
        await db.collection('Wervingsregistraties').doc(selected.docid).update({
            ...registratieUpdate(form),
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        })

        // Auto-register as a household — once only (guarded by HouseholdAdresID).
        if (form.UitkomstDetail === AUTO_REGISTER_DETAIL && !selected.HouseholdAdresID) {
            const householdId = await createHousehold(selected, form)
            await db.collection('Wervingsregistraties').doc(selected.docid).update({ HouseholdAdresID: householdId })
        }

        setSaved('flex')
        setEditorOpen(false)
        setSelected(null)
    }

    return (
        <>
        {(registraties || []).length > 0 && (
            <div className='wa-filters'>
                <label htmlFor='wa-filter-buurt'>Buurt</label>
                <select
                    id='wa-filter-buurt'
                    value={buurt}
                    onChange={(e) => {
                        const value = e.target.value
                        setBuurtKeuze(value)
                        setStraatKeuze('')
                    }}
                >
                    <option value=''>Alle buurten</option>
                    {buurtOpties.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <label htmlFor='wa-filter-straat'>Straat</label>
                <select id='wa-filter-straat' value={straat} onChange={(e) => setStraatKeuze(e.target.value)}>
                    <option value=''>Alle straten</option>
                    {straatOpties.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>
        )}
        <div className='table-container'>
            <Table
                columns={COLUMNS}
                items={items}
                getItemKey={(r) => r.ID}
                renderRow={(r) => (
                    <>
                        <TableCell>
                            {canEdit ? (
                                <p className='wa-link' onClick={() => openEdit(r)}>{adresLabel(r) || '—'}</p>
                            ) : (
                                <p>{adresLabel(r) || '—'}</p>
                            )}
                        </TableCell>
                        <TableCell><p>{r.Postcode || '—'}</p></TableCell>
                        <TableCell><p>{r.BriefGepost ? '✓' : '–'}</p></TableCell>
                        <TableCell><p>{r.DatumBriefGepost || '—'}</p></TableCell>
                        <TableCell><p>{r.Deurgesprek ? '✓' : '–'}</p></TableCell>
                        <TableCell><p>{r.Datum || '—'}</p></TableCell>
                        <TableCell><p>{uitkomstMetDiensten(r, dienstNamen)}</p></TableCell>
                        <TableCell><p>{woningtypeLabel(r.Woningtype)}</p></TableCell>
                    </>
                )}
                searchAccessor={adresLabel}
                searchPlaceholder='Zoeken op adres'
                emptyMessage="Nog geen adressen gekozen. Kies eerst adressen onder 'Gebied'."
            />

            <RegistratieModal
                modalOpen={editorOpen}
                setModalOpen={setEditorOpen}
                diensten={diensten}
                dienstNamen={dienstNamen}
                registratie={selected}
                onSave={saveRegistratie}
            />
        </div>
        </>
    )
}

export default Registratie
